import { useState } from 'react';
import axios from 'axios';
import { History, Search, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => `${n < 0 ? '-' : ''}$${Math.abs(Number(n || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const decimal = (a) => (a > 0 ? a / 100 + 1 : 100 / Math.abs(a) + 1);
const STATUS_CLS = { won: 'text-[#39FF14]', lost: 'text-[#FF6B6B]', void: 'text-[#A0AAB2]' };
const GRADES = [['won', 'Win', 'bg-[#39FF14]/15 text-[#39FF14] border-[#39FF14]/40'], ['lost', 'Loss', 'bg-[#FF3B30]/15 text-[#FF6B6B] border-[#FF3B30]/40'], ['void', 'Void', 'bg-white/10 text-[#A0AAB2] border-white/20']];

// Mirrors backend _ticket_result (auto-settler rule).
function preview(b, g) {
    const grades = b.legs.map((l) => g[l.index]);
    if (grades.some((x) => !x || x === 'pending')) return null;
    if (b.type !== 'parlay') {
        const r = grades[0];
        return { status: r, paid: r === 'won' ? b.potential_payout : r === 'void' ? b.amount : 0 };
    }
    if (grades.includes('lost')) return { status: 'lost', paid: 0 };
    const won = b.legs.filter((l) => g[l.index] === 'won');
    if (!won.length) return { status: 'void', paid: b.amount };
    const dec = won.reduce((acc, l) => acc * decimal(l.odds || -110), 1);
    const paid = Math.min(b.amount * dec * (b.sgp_factor || 1), b.potential_payout);
    return { status: 'won', paid: Math.round(paid * 100) / 100 };
}

function LegRow({ b, l, value, onPick }) {
    return (
        <div className="flex items-center justify-between gap-2 bg-black/30 rounded-md px-2.5 py-1.5" data-testid={`regrade-leg-${b.id}-${l.index}`}>
            <div className="min-w-0">
                <p className="text-[11px] text-white truncate">{l.description && !(l.outcome_name || '').toLowerCase().startsWith(l.description.toLowerCase()) ? `${l.description} ` : ''}{l.outcome_name}</p>
                <p className="text-[10px] text-[#A0AAB2] truncate">{l.market_label}{l.away_team ? ` · ${l.away_team} @ ${l.home_team}` : ''}</p>
            </div>
            <div className="flex gap-1 shrink-0">
                {GRADES.map(([k, label, cls]) => (
                    <button key={k} type="button" onClick={() => onPick(k)} data-testid={`regrade-leg-${b.id}-${l.index}-${k}`}
                        className={`text-[10px] px-2 py-1 rounded-md border transition-colors ${value === k ? cls : 'border-white/10 text-[#666] hover:text-white hover:border-white/30'}`}>{label}</button>
                ))}
            </div>
        </div>
    );
}

function TicketCard({ b, onDone }) {
    const [g, setG] = useState(() => Object.fromEntries(b.legs.map((l) => [l.index, l.grade])));
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const pv = preview(b, g);
    const delta = pv ? Math.round((pv.paid - b.paid) * 100) / 100 : 0;
    const changed = pv && (pv.status !== b.status || delta !== 0);
    const submit = async () => {
        const msg = `Correct this ticket?\n\n${b.status.toUpperCase()} → ${pv.status.toUpperCase()}\n${delta > 0 ? `Pay the player ${fmt(delta)}` : delta < 0 ? `Take back ${fmt(-delta)} (balance may go negative)` : 'No money moves'}\n\nReason: ${reason}`;
        if (!window.confirm(msg)) return;
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/admin/sports/regrade/${b.id}`, { grades: b.legs.map((l) => ({ index: l.index, grade: g[l.index] })), reason }, { withCredentials: true });
            toast.success(`Corrected to ${data.status.toUpperCase()} · ${data.delta >= 0 ? 'paid' : 'took back'} ${fmt(Math.abs(data.delta))} · player balance ${fmt(data.player_balance)}`);
            onDone();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Correction failed');
        } finally { setBusy(false); }
    };
    return (
        <div className="rounded-lg border border-white/10 bg-[#0e0e0e] p-3 space-y-2" data-testid={`regrade-bet-${b.id}`}>
            <div className="flex items-center justify-between text-xs flex-wrap gap-1">
                <span className="text-white font-bold">{b.name}<span className="ml-2 text-[9px] uppercase px-1.5 py-0.5 rounded-full bg-white/10 text-[#A0AAB2]">{b.type}</span>
                    <span className={`ml-2 text-[10px] uppercase font-bold ${STATUS_CLS[b.status]}`} data-testid={`regrade-status-${b.id}`}>{b.status}</span></span>
                <span className="font-mono text-[#A0AAB2]">stake {fmt(b.amount)} · paid {fmt(b.paid)} · #{b.id.slice(-6)}</span>
            </div>
            {b.legs.map((l) => <LegRow key={l.index} b={b} l={l} value={g[l.index]} onPick={(k) => setG((p) => ({ ...p, [l.index]: k }))} />)}
            {b.corrections.length > 0 && (
                <div className="text-[10px] text-[#A0AAB2] space-y-0.5" data-testid={`regrade-history-${b.id}`}>
                    {b.corrections.map((c, i) => <p key={i}><History className="inline w-3 h-3 mr-1" />{new Date(c.at).toLocaleString()} · {c.by_email} · {c.from_status} → {c.to_status} ({fmt(c.delta)}) · “{c.reason}”</p>)}
                </div>
            )}
            <div className="flex items-center gap-2 flex-wrap pt-1">
                <span className={`text-[11px] font-bold ${pv ? STATUS_CLS[pv.status] : 'text-[#666]'}`} data-testid={`regrade-preview-${b.id}`}>
                    {!pv ? 'Grade every leg' : !changed ? 'No change' : `${pv.status.toUpperCase()} · ${delta > 0 ? `pay ${fmt(delta)}` : delta < 0 ? `take back ${fmt(-delta)}` : 'no money moves'}`}
                </span>
                <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" data-testid={`regrade-reason-${b.id}`}
                    className="flex-1 min-w-[160px] bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-[11px] text-white placeholder:text-[#555]" />
                <button type="button" onClick={submit} disabled={busy || !changed || reason.trim().length < 3} data-testid={`regrade-submit-${b.id}`}
                    className="text-[11px] px-3 py-1.5 rounded-md bg-[#FFD700] text-black font-bold hover:bg-[#e6c200] disabled:opacity-40 disabled:cursor-not-allowed">
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Apply correction'}
                </button>
            </div>
        </div>
    );
}

export default function FixGradePanel() {
    const [q, setQ] = useState('');
    const [bets, setBets] = useState(null);
    const [loading, setLoading] = useState(false);
    const search = async (e) => {
        e?.preventDefault();
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/sports/regrade/search`, { params: { q }, withCredentials: true });
            setBets(data.bets || []);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Search failed');
        } finally { setLoading(false); }
    };
    return (
        <div className="card-dark p-5 space-y-4" data-testid="fix-grade-panel">
            <div className="flex items-center gap-2">
                <History className="w-5 h-5 text-[#FFD700]" />
                <h3 className="text-sm uppercase font-bold tracking-wide text-white">Fix a graded ticket</h3>
            </div>
            <div className="flex items-start gap-2 text-[11px] text-[#FFD700] bg-[#FFD700]/5 border border-[#FFD700]/20 rounded-lg p-2.5">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>Find a settled ticket, change the leg grades, give a reason and apply. Only the difference moves: lost → won pays the winnings, won → lost takes them back (the balance may go negative). Cashed-out tickets can't be changed. Every correction is logged and the player is notified.</span>
            </div>
            <form onSubmit={search} className="flex gap-2">
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ticket ID, player email or name (blank = latest)" data-testid="regrade-search-input"
                    className="flex-1 bg-black/40 border border-white/10 rounded-md px-3 py-2 text-xs text-white placeholder:text-[#555]" />
                <button type="submit" disabled={loading} data-testid="regrade-search-btn"
                    className="flex items-center gap-1 text-xs px-3 py-2 rounded-md border border-[#FFD700]/30 text-[#FFD700] hover:bg-[#FFD700]/10">
                    {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}Search
                </button>
            </form>
            {bets && bets.length === 0 && <p className="text-[11px] text-[#A0AAB2] italic" data-testid="regrade-empty">No settled tickets found.</p>}
            {bets && bets.map((b) => <TicketCard key={`${b.id}-${b.corrections.length}-${b.status}`} b={b} onDone={search} />)}
        </div>
    );
}
